// Pooled expanding death rings: a clear, tinted shockwave per kill.
// One instanced draw call; all timing derives from sim ticks.

import * as THREE from 'three';
import type { Vec2 } from '../types.js';

const MAX_RINGS = 64;
const RING_LIFE_TICKS = 24; // ~0.4 s at 60 Hz

interface RingSlot {
  active: boolean;
  x: number;
  y: number;
  startTick: number;
  color: THREE.Color;
}

export class DeathRings {
  readonly mesh: THREE.InstancedMesh;
  private readonly slots: RingSlot[] = [];
  private readonly dummy = new THREE.Object3D();
  private readonly colorAttr: THREE.InstancedBufferAttribute;

  constructor() {
    const geometry = new THREE.RingGeometry(0.85, 1.0, 48, 1);
    this.colorAttr = new THREE.InstancedBufferAttribute(new Float32Array(MAX_RINGS * 3), 3);
    this.colorAttr.setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute('aColor', this.colorAttr);

    const material = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    material.onBeforeCompile = (shader) => {
      shader.vertexShader = shader.vertexShader.replace(
        '#include <common>',
        `#include <common>
attribute vec3 aColor;
varying vec3 vColor;`
      ).replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
vColor = aColor;`
      );
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <common>',
        `#include <common>
varying vec3 vColor;`
      ).replace(
        'vec4 diffuseColor = vec4( diffuse, opacity );',
        'vec4 diffuseColor = vec4( vColor, opacity );'
      );
    };

    this.mesh = new THREE.InstancedMesh(geometry, material, MAX_RINGS);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    this.mesh.renderOrder = 3;

    for (let i = 0; i < MAX_RINGS; i++) {
      this.slots.push({ active: false, x: 0, y: 0, startTick: 0, color: new THREE.Color(1, 1, 1) });
    }
  }

  spawn(pos: Vec2, color: THREE.Color, tick: number): void {
    const slot = this.slots.find((s) => !s.active) ?? this.slots[0];
    slot.active = true;
    slot.x = pos.x;
    slot.y = pos.y;
    slot.startTick = tick;
    slot.color.copy(color);
  }

  update(tick: number, alpha: number): void {
    const t = tick + alpha;
    let count = 0;
    for (const slot of this.slots) {
      if (!slot.active) continue;
      const age = t - slot.startTick;
      if (age >= RING_LIFE_TICKS || age < 0) {
        slot.active = false;
        continue;
      }
      const k = age / RING_LIFE_TICKS;
      const ease = 1 - Math.pow(1 - k, 3);
      const radius = 0.2 + ease * 2.4;
      const alphaFade = (1 - k) * 0.9;
      this.dummy.position.set(slot.x, slot.y, 0.06);
      this.dummy.scale.setScalar(radius);
      this.dummy.updateMatrix();
      this.mesh.setMatrixAt(count, this.dummy.matrix);
      this.colorAttr.setXYZ(
        count,
        slot.color.r * alphaFade,
        slot.color.g * alphaFade,
        slot.color.b * alphaFade
      );
      count++;
    }
    this.mesh.count = count;
    if (count > 0) {
      this.mesh.instanceMatrix.needsUpdate = true;
      this.colorAttr.needsUpdate = true;
    }
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
